import React, { createContext, useContext, useState, ReactNode } from 'react';

interface MetricTranslation {
  technical: string;
  founder: string;
  tooltip: string;
}

// Mapping of technical terms to founder-friendly language
const metricTranslations: Record<string, MetricTranslation> = {
  // Supply Chain Metrics
  'resilience_score': {
    technical: 'Resilience Score',
    founder: 'Supply Chain Strength',
    tooltip: 'How well your supply chain handles unexpected problems',
  },
  'carbon_footprint': {
    technical: 'Carbon Footprint',
    founder: 'Environmental Impact',
    tooltip: 'How much pollution your operations create',
  },
  'supplier_risk': {
    technical: 'Supplier Risk Index',
    founder: 'Supplier Reliability',
    tooltip: 'How likely your suppliers are to cause problems',
  },
  'inventory_turnover': {
    technical: 'Inventory Turnover Rate',
    founder: 'Stock Flow Speed',
    tooltip: 'How fast your products move from warehouse to sale',
  },
  'lead_time': {
    technical: 'Lead Time',
    founder: 'Delivery Wait Time',
    tooltip: 'How long it takes to get products from order to delivery',
  },
  'stockout_rate': {
    technical: 'Stockout Rate',
    founder: 'Out-of-Stock Events',
    tooltip: 'How often you run out of products customers want',
  },
  'carrying_cost': {
    technical: 'Carrying Cost',
    founder: 'Storage Expenses',
    tooltip: 'How much it costs to keep products in your warehouse',
  },
  'fill_rate': {
    technical: 'Fill Rate',
    founder: 'Order Completion Rate',
    tooltip: 'Percentage of orders you can ship immediately',
  },
  'perfect_order_rate': {
    technical: 'Perfect Order Rate',
    founder: 'Happy Customer Orders',
    tooltip: 'Orders delivered correctly, on time, without damage',
  },
  'cash_to_cash_cycle': {
    technical: 'Cash-to-Cash Cycle Time',
    founder: 'Money Return Speed',
    tooltip: 'How long your money is tied up in inventory',
  },
  'days_of_supply': {
    technical: 'Days of Supply (DOS)',
    founder: 'Stock Runway',
    tooltip: 'How many days of stock you have before running out',
  },
  'supplier_lead_time_variability': {
    technical: 'Supplier Lead Time Variability',
    founder: 'Supplier Predictability',
    tooltip: 'How consistent your suppliers are with delivery times',
  },
  'capacity_utilization': {
    technical: 'Capacity Utilization',
    founder: 'How Busy You Are',
    tooltip: 'Percentage of your maximum production being used',
  },
  'sustainability_score': {
    technical: 'Sustainability Score',
    founder: 'Green Rating',
    tooltip: 'How eco-friendly your operations are',
  },
  'disruption_impact': {
    technical: 'Disruption Impact Assessment',
    founder: 'Problem Damage Level',
    tooltip: 'How badly problems affect your operations',
  },
};

type Mode = 'analyst' | 'founder';

interface FounderModeContextType {
  mode: Mode;
  toggleMode: () => void;
  setMode: (mode: Mode) => void;
  translate: (key: string, fallback?: string) => string;
  getTooltip: (key: string) => string;
}

const FounderModeContext = createContext<FounderModeContextType | undefined>(undefined);

export const FounderModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<Mode>(() => {
    const saved = localStorage.getItem('ascin_display_mode');
    return (saved as Mode) || 'founder'; // Default to founder-friendly mode
  });

  const setMode = (newMode: Mode) => {
    setModeState(newMode);
    localStorage.setItem('ascin_display_mode', newMode);
  };

  const toggleMode = () => {
    setMode(mode === 'analyst' ? 'founder' : 'analyst');
  };

  const translate = (key: string, fallback?: string): string => {
    const normalizedKey = key.toLowerCase().replace(/\s+/g, '_');
    const translation = metricTranslations[normalizedKey];
    
    if (!translation) {
      return fallback || key;
    }
    
    return mode === 'analyst' ? translation.technical : translation.founder;
  };

  const getTooltip = (key: string): string => {
    const normalizedKey = key.toLowerCase().replace(/\s+/g, '_');
    const translation = metricTranslations[normalizedKey];
    return translation?.tooltip || '';
  };

  return (
    <FounderModeContext.Provider value={{ mode, toggleMode, setMode, translate, getTooltip }}>
      {children}
    </FounderModeContext.Provider>
  );
};

export const useFounderMode = (): FounderModeContextType => {
  const context = useContext(FounderModeContext);
  if (!context) {
    throw new Error('useFounderMode must be used within a FounderModeProvider');
  }
  return context;
};

export default FounderModeProvider;
